import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ApiError } from '@/shared/api/client';
import { parametrization } from '@/shared/api/resources';
import { Alert } from '@/shared/ui/Alert';
import { Button } from '@/shared/ui/Button';
import { Modal } from '@/shared/ui/Modal';
import { Textarea } from '@/shared/ui/Textarea';

import { fieldErrorOf } from '../shared/fieldError';

import { AVISO_AUTOAPROVACAO, autoriaDe } from './autoaprovacao';

/**
 * Ramo, grupo ou modalidade em rascunho que a pessoa quer ativar (P-CARGA).
 *
 * `autor` vem do servidor (`draft_authored_by_you`): quem criou, carregou ou editou o
 * rascunho nao ativa. A tela explica e desliga o botao; quem recusa e o servidor.
 * `autoaprovacao` (`draft_may_self_approve`) diz que a pessoa autora foi liberada
 * nominalmente da segregacao (ADR 0091): o botao liga, com o aviso.
 *
 * `bloqueio` e o que a tela ja sabe que o servidor recusaria (R5-B32): o ramo ou o
 * grupo ainda em rascunho, ou a modalidade sem taxa base. A configuracao aprovada so o
 * servidor confere.
 */
export interface ItemEmRascunho {
  tipo: 'ramo' | 'grupo' | 'modalidade';
  id: string;
  nome: string;
  autor: boolean;
  autoaprovacao?: boolean | undefined;
  bloqueio?: string | null | undefined;
}

const titulos: Record<ItemEmRascunho['tipo'], string> = {
  ramo: 'Ativar ramo',
  grupo: 'Ativar grupo',
  modalidade: 'Ativar modalidade',
};

// O efeito de cada ativacao, na ordem ramo, grupo e modalidade da carga com aprovacao.
const efeitos: Record<ItemEmRascunho['tipo'], string> = {
  ramo: 'O ramo sai do rascunho. Os grupos e as modalidades dele continuam em rascunho ate a ativacao de cada um.',
  grupo:
    'O grupo sai do rascunho. O ramo dele precisa ja ter saido do rascunho, e as modalidades continuam em rascunho ate a ativacao de cada uma.',
  modalidade:
    'A modalidade sai do rascunho e passa a aparecer na cotacao. O ramo e o grupo precisam ja ter saido do rascunho, e a configuracao (Detalhes da modalidade) precisa de versao aprovada. A taxa base so e exigida quando a configuracao usa taxa por modalidade.',
};

function ativarItem(item: ItemEmRascunho, justificativa: string): Promise<unknown> {
  switch (item.tipo) {
    case 'ramo':
      return parametrization.activateProduct(item.id, justificativa);
    case 'grupo':
      return parametrization.activateGroup(item.id, justificativa);
    case 'modalidade':
      return parametrization.activateModality(item.id, justificativa);
  }
}

/**
 * O que a ativacao muda e que outras telas guardam: a lista de ramos, os grupos, o
 * detalhe e as listas de modalidades e as opcoes da cotacao da corretora (R5-B31).
 */
const chavesDaAtivacao = [
  ['products'],
  ['modality-groups'],
  ['modality'],
  ['modalities'],
  ['broker-quote-options'],
] as const;

/**
 * Dialogo "Ativar" do rascunho: justificativa obrigatoria, que vai para a trilha com
 * quem ativou e quem preparou o rascunho.
 *
 * Depois da ativacao, e tambem quando ela falha, as leituras guardadas sao refeitas:
 * o detalhe da modalidade mostrava "rascunho" por ate 30 s, e "Salvar detalhes"
 * mandava a situacao velha e recebia 409. Na falha, a situacao pode ter mudado por
 * outra pessoa (R5-B31).
 */
export function AtivacaoDoRascunho({
  item,
  onFechar,
  onAtivado,
}: {
  item: ItemEmRascunho | null;
  onFechar: () => void;
  onAtivado: (item: ItemEmRascunho) => void;
}) {
  const queryClient = useQueryClient();
  const [justificativa, setJustificativa] = useState('');
  const ativar = useMutation({
    mutationFn: (alvo: ItemEmRascunho) => ativarItem(alvo, justificativa),
    onSuccess: (_resposta, alvo) => {
      setJustificativa('');
      onAtivado(alvo);
    },
    onSettled: () => {
      for (const chave of chavesDaAtivacao) {
        void queryClient.invalidateQueries({ queryKey: [...chave] });
      }
    },
  });
  const apiError = ativar.error instanceof ApiError ? ativar.error : null;
  const autoria = autoriaDe(item?.autor, item?.autoaprovacao);

  function fechar() {
    ativar.reset();
    setJustificativa('');
    onFechar();
  }

  return (
    <Modal
      open={item !== null}
      onClose={fechar}
      title={item ? `${titulos[item.tipo]}: ${item.nome}` : ''}
      description={item ? efeitos[item.tipo] : undefined}
    >
      {item ? (
        <form
          className="flex flex-col gap-4"
          onSubmit={(evento) => {
            evento.preventDefault();
            ativar.mutate(item);
          }}
        >
          {autoria.barrada ? (
            <Alert tone="warning" title="Ativacao por outra pessoa">
              Voce criou, carregou ou editou este rascunho. A ativacao e de outra pessoa
              com a alcada (segregacao de funcoes).
            </Alert>
          ) : item.bloqueio ? (
            <Alert tone="warning" title="Ainda nao pode ser ativado">
              {item.bloqueio}
            </Alert>
          ) : null}
          {autoria.autoaprovacao && !item.bloqueio ? (
            <Alert tone="warning" title="Autoaprovacao liberada">
              {AVISO_AUTOAPROVACAO}
            </Alert>
          ) : null}
          {ativar.error && !fieldErrorOf(apiError, 'justification') ? (
            <Alert tone="danger" title="Nao foi possivel ativar">
              {ativar.error.message}
            </Alert>
          ) : null}
          <Textarea
            label="Justificativa da ativacao"
            required
            value={justificativa}
            disabled={autoria.barrada || Boolean(item.bloqueio)}
            error={fieldErrorOf(apiError, 'justification')}
            onChange={(evento) => setJustificativa(evento.target.value)}
          />
          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <Button type="button" variant="secondary" onClick={fechar}>
              Fechar
            </Button>
            <Button
              type="submit"
              loading={ativar.isPending}
              disabled={
                autoria.barrada || Boolean(item.bloqueio) || justificativa.trim() === ''
              }
              title={item.bloqueio ?? undefined}
            >
              Ativar
            </Button>
          </div>
        </form>
      ) : null}
    </Modal>
  );
}
